import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import {
  BaseServerTableProps,
  TableFilters,
  TableResponse,
  TableSort,
  TableSortDirection,
} from './types';

type UseServerTableOptions<Row> = Pick<
  BaseServerTableProps<Row>,
  'fetchData' | 'pageSize' | 'initialSort' | 'initialFilters' | 'filterDebounceMs' | 'onError'
> & {
  onResponse: (response: TableResponse<Row>, requestedPage: number, replace: boolean) => void;
  onReset: () => void;
};

const useServerTable = <Row,>({
  fetchData,
  pageSize = 50,
  initialSort,
  initialFilters = {},
  filterDebounceMs = 400,
  onError,
  onResponse,
  onReset,
}: UseServerTableOptions<Row>) => {
  const [sort, setSort] = useState<TableSort | undefined>(initialSort);
  const [draftFilters, setDraftFilters] = useState<TableFilters>(initialFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>();
  const requestIdRef = useRef(0);
  const loadingRef = useRef(false);
  const controllerRef = useRef<AbortController | null>(null);
  const filtersRef = useRef(initialFilters);
  const sortRef = useRef(sort);

  const loadPage = useCallback(
    async (requestedPage: number, replace: boolean, filters = filtersRef.current, nextSort = sortRef.current) => {
      if (loadingRef.current && !replace) return;

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const { signal } = controller;
      const requestId = ++requestIdRef.current;
      loadingRef.current = true;
      setLoading(true);
      setError(undefined);
      if (replace) onReset();

      try {
        const response = await fetchData({
          page: requestedPage,
          pageSize,
          offset: requestedPage * pageSize,
          sort: nextSort,
          filters,
          signal,
        });
        if (!signal.aborted && requestId === requestIdRef.current) {
          onResponse(response, requestedPage, replace);
        }
      } catch (requestError) {
        if (!signal.aborted && requestId === requestIdRef.current) {
          setError(requestError);
          onError?.(requestError);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          controllerRef.current = null;
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [fetchData, onError, onReset, onResponse, pageSize],
  );

  const debouncedFilterLoad = useMemo(
    () => debounce((filters: TableFilters) => loadPage(0, true, filters), filterDebounceMs),
    [filterDebounceMs, loadPage],
  );

  useEffect(() => {
    loadPage(0, true);
    return () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, [loadPage]);

  useEffect(() => () => debouncedFilterLoad.cancel(), [debouncedFilterLoad]);

  const retryLoad = useCallback(() => {
    debouncedFilterLoad.cancel();
    loadPage(0, true);
  }, [debouncedFilterLoad, loadPage]);

  const cancelPendingFilterLoad = useCallback(() => {
    debouncedFilterLoad.cancel();
  }, [debouncedFilterLoad]);

  const updateSort = useCallback((column: string, defaultDirection: TableSortDirection = 'ASC') => {
    const currentSort = sortRef.current;
    const nextSort: TableSort = {
      column,
      direction: currentSort?.column === column
        ? (currentSort.direction === 'ASC' ? 'DESC' : 'ASC')
        : defaultDirection,
    };
    sortRef.current = nextSort;
    setSort(nextSort);
    debouncedFilterLoad.cancel();
    loadPage(0, true, filtersRef.current, nextSort);
  }, [debouncedFilterLoad, loadPage]);

  const updateFilterValue = useCallback(
    (column: string, value: TableFilters[string], shouldDebounce: boolean) => {
      const nextFilters = { ...filtersRef.current, [column]: value };
      filtersRef.current = nextFilters;
      setDraftFilters(nextFilters);

      if (shouldDebounce) {
        debouncedFilterLoad(nextFilters);
      } else {
        debouncedFilterLoad.cancel();
        loadPage(0, true, nextFilters);
      }
    },
    [debouncedFilterLoad, loadPage],
  );

  const updateTypedFilter = useCallback(
    (column: string, value: TableFilters[string]) => {
      updateFilterValue(column, value, typeof value === 'string');
    },
    [updateFilterValue],
  );

  const updateFilter = useCallback(
    (column: string, event: ChangeEvent<HTMLInputElement>) => {
      updateFilterValue(column, event.target.value, true);
    },
    [updateFilterValue],
  );

  const clearFilters = useCallback(() => {
    filtersRef.current = initialFilters;
    setDraftFilters(initialFilters);
    debouncedFilterLoad.cancel();
    loadPage(0, true, initialFilters);
  }, [debouncedFilterLoad, initialFilters, loadPage]);

  return {
    cancelPendingFilterLoad,
    clearFilters,
    draftFilters,
    error,
    loadPage,
    loading,
    loadingRef,
    retryLoad,
    sort,
    updateFilter,
    updateSort,
    updateTypedFilter,
  };
};

export default useServerTable;
